import React from 'react';
import { cn } from '@/lib/utils';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import type { SubNavGroup } from './types';
import { NavigationLink } from '../Navigation/NavigationLink';

export interface SubNavAccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  navigationData: SubNavGroup[];
  activeItem?: string;
  activeTab?: string;
  onItemClick?: (groupTitle: string, itemHref: string) => void;
  /** Custom link component for routing (e.g., React Router Link) */
  linkComponent?: React.ElementType;
}

export const SubNavAccordion = React.forwardRef<HTMLDivElement, SubNavAccordionProps>(
  ({ 
    navigationData, 
    activeItem,
    activeTab, 
    onItemClick,
    linkComponent,
    className,
    ...props 
  }, ref) => {
    
    if (!navigationData || navigationData.length === 0) {
      return null;
    }

    const activeGroup = navigationData.find(group => 
      group.title?.toLowerCase() === activeTab?.toLowerCase()
    );

    return (
      <div 
        ref={ref}
        className={cn(
          "bg-white rounded-2xl p-4 shadow-sm border border-gray-200",
          className
        )}
        {...props}
      >
        <Accordion 
          type="single" 
          collapsible 
          defaultValue={activeGroup?.title || navigationData[0]?.title}
          className="w-full"
        >
          {navigationData.map((group) => (
            <AccordionItem 
              key={group.title} 
              value={group.title}
              className="border-b border-gray-100 last:border-0"
            >
              <AccordionTrigger className="text-sm font-medium text-gray-900 hover:text-sky-700">
                {group.title}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-1 pt-2">
                  {group.items.map((item) => (
                    linkComponent ? (
                      <NavigationLink
                        key={item.href}
                        as={linkComponent}
                        href={item.href}
                        className={cn(
                          "block w-full text-left font-normal px-2 py-2 text-sm rounded-md",
                          "text-gray-700 hover:text-gray-900 hover:bg-gray-50",
                          activeItem === item.href && "text-sky-700 bg-sky-50"
                        )}
                        aria-current={activeItem === item.href ? 'page' : undefined}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          onItemClick?.(group.title, item.href);
                        }}
                      >
                        {item.label}
                      </NavigationLink>
                    ) : (
                      <Button
                        key={item.href}
                        variant="ghost"
                        className={cn(
                          "w-full justify-start text-left font-normal px-2 py-2 h-auto text-sm rounded-md",
                          "text-gray-700 hover:text-gray-900 hover:bg-gray-50",
                          activeItem === item.href && "text-sky-700 bg-sky-50"
                        )}
                        type="button"
                        aria-current={activeItem === item.href ? 'page' : undefined}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          onItemClick?.(group.title, item.href);
                        }}
                      >
                        {item.label}
                      </Button>
                    )
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    );
  }
);

SubNavAccordion.displayName = 'SubNavAccordion';
